import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, CaretDown, CaretUp, GraduationCap, NotePencil } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MODE_COLOR = { online: '#2B6EF0', hybrid: '#8B5CF6', offline: '#10A882' };

export default function CoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourse, setShowCourse] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [cf, setCf] = useState({ name: '', category: '', duration: '', mode: 'offline', price: 0 });
  const [bf, setBf] = useState({ course_id: '', branch_id: '', name: '', start_date: '', end_date: '', timing: '', capacity: 30 });
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [enrollmentStats, setEnrollmentStats] = useState({});
  const [enrollmentLoading, setEnrollmentLoading] = useState({});
  const [editingCourse, setEditingCourse] = useState(null);
  const [ef, setEf] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [ebf, setEbf] = useState(null);
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);

  const fetchAll = async () => {
    try {
      const [c, b, br] = await Promise.all([api.get('/api/courses'), api.get('/api/batches'), api.get('/api/branches')]);
      setCourses(c.data.courses || []); setBatches(b.data.batches || []); setBranches(br.data.branches || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleCourse = async (e) => { e.preventDefault(); try { await api.post('/api/courses', cf); toast.success('Created'); setShowCourse(false); setCf({ name: '', category: '', duration: '', mode: 'offline', price: 0 }); fetchAll(); } catch { toast.error('Failed'); } };
  const handleBatch = async (e) => { e.preventDefault(); try { await api.post('/api/batches', bf); toast.success('Created'); setShowBatch(false); setBf({ course_id: '', branch_id: '', name: '', start_date: '', end_date: '', timing: '', capacity: 30 }); fetchAll(); } catch { toast.error('Failed'); } };

  // CoursesPage previously had no edit path at all -- courses could only ever be
  // created, never changed. Pre-fills from the course's real fields (one_time_fee /
  // installment_total / installment_schedule / duration_months), which the legacy
  // "Add Course" form above never touches (it only sets the older price/duration/mode
  // trio, kept in sync with one_time_fee/duration_months at seed time).
  const openEditCourse = (c) => {
    setEditingCourse(c);
    setEf({
      name: c.name || '',
      category: c.category || '',
      mode: c.mode || 'offline',
      duration_months: c.duration_months ?? '',
      one_time_fee: c.one_time_fee ?? c.price ?? 0,
      installment_total: c.installment_total ?? '',
      installment_schedule: Array.isArray(c.installment_schedule) ? c.installment_schedule.join(', ') : '',
      status: c.status || 'active',
      branch_ids: c.branch_ids || [],
    });
  };

  const toggleEditBranch = (branchId) => {
    setEf(prev => {
      const ids = prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId];
      return { ...prev, branch_ids: ids };
    });
  };

  const handleEditCourseSave = async (e) => {
    e.preventDefault();
    if (!editingCourse) return;
    const scheduleParts = ef.installment_schedule.split(',').map(s => s.trim()).filter(Boolean);
    const schedule = [];
    for (const part of scheduleParts) {
      const n = Number(part);
      if (Number.isNaN(n)) { toast.error(`"${part}" in the installment schedule isn't a number`); return; }
      schedule.push(n);
    }
    const oneTimeFee = Number(ef.one_time_fee) || 0;
    const payload = {
      name: ef.name,
      category: ef.category,
      mode: ef.mode,
      status: ef.status,
      branch_ids: ef.branch_ids,
      one_time_fee: oneTimeFee,
      price: oneTimeFee, // keep the legacy list-view field in sync so it never goes stale
      duration_months: ef.duration_months === '' ? null : Number(ef.duration_months),
      installment_total: ef.installment_total === '' ? null : Number(ef.installment_total),
      installment_schedule: schedule,
    };
    setSavingEdit(true);
    try {
      await api.put(`/api/courses/${editingCourse.id}`, payload);
      toast.success('Course updated');
      setEditingCourse(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update course');
    } finally {
      setSavingEdit(false);
    }
  };

  const openEditBatch = (b) => {
    setEditingBatch(b);
    setEbf({
      name: b.name || '',
      course_id: b.course_id || '',
      branch_id: b.branch_id || '',
      capacity: b.capacity ?? 30,
      start_date: b.start_date || '',
      end_date: b.end_date || '',
      timing: b.timing || '',
      status: b.status || 'active',
    });
  };

  const handleEditBatchSave = async (e) => {
    e.preventDefault();
    if (!editingBatch) return;
    setSavingBatchEdit(true);
    try {
      await api.put(`/api/batches/${editingBatch.id}`, {
        ...ebf,
        capacity: Number(ebf.capacity) || 0,
      });
      toast.success('Batch updated');
      setEditingBatch(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update batch');
    } finally {
      setSavingBatchEdit(false);
    }
  };

  const toggleCourseRow = async (courseId) => {
    if (expandedCourseId === courseId) { setExpandedCourseId(null); return; }
    setExpandedCourseId(courseId);
    if (enrollmentStats[courseId]) return;
    setEnrollmentLoading((prev) => ({ ...prev, [courseId]: true }));
    try {
      const { data } = await api.get(`/api/courses/${courseId}/enrollment-stats`);
      setEnrollmentStats((prev) => ({ ...prev, [courseId]: data }));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load enrollment stats');
    } finally {
      setEnrollmentLoading((prev) => ({ ...prev, [courseId]: false }));
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  return (
    <div data-testid="courses-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Courses & Batches</h1>
          <p className="text-sm text-muted-foreground mt-1"><span className="font-mono">{courses.length}</span> courses &middot; <span className="font-mono">{batches.length}</span> batches</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCourse} onOpenChange={setShowCourse}>
            <DialogTrigger asChild><Button data-testid="add-course-btn" variant="outline" className="h-9 rounded-lg text-xs gap-1"><Plus size={14} /> Course</Button></DialogTrigger>
            <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add Course</DialogTitle></DialogHeader>
              <form onSubmit={handleCourse} className="space-y-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label><Input data-testid="course-name" value={cf.name} onChange={e => setCf({...cf, name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Category</Label><Input value={cf.category} onChange={e => setCf({...cf, category: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Duration</Label><Input value={cf.duration} onChange={e => setCf({...cf, duration: e.target.value})} className="h-9 rounded-lg text-sm" placeholder="2 Years" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Mode</Label>
                    <select value={cf.mode} onChange={e => setCf({...cf, mode: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                      <option value="offline">Offline</option><option value="online">Online</option><option value="hybrid">Hybrid</option>
                    </select></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Price (Rs.)</Label><Input type="number" value={cf.price} onChange={e => setCf({...cf, price: parseFloat(e.target.value) || 0})} className="h-9 rounded-lg text-sm" /></div>
                </div>
                <Button type="submit" data-testid="submit-course-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showBatch} onOpenChange={setShowBatch}>
            <DialogTrigger asChild><Button data-testid="add-batch-btn" className="h-9 rounded-lg text-xs gap-1 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}><Plus size={14} /> Batch</Button></DialogTrigger>
            <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add Batch</DialogTitle></DialogHeader>
              <form onSubmit={handleBatch} className="space-y-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Course *</Label>
                  <select data-testid="batch-course" value={bf.course_id} onChange={e => setBf({...bf, course_id: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required><option value="">Select</option>{courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branch *</Label>
                  <select data-testid="batch-branch" value={bf.branch_id} onChange={e => setBf({...bf, branch_id: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required><option value="">Select</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Batch Name *</Label><Input data-testid="batch-name" value={bf.name} onChange={e => setBf({...bf, name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Capacity</Label><Input type="number" value={bf.capacity} onChange={e => setBf({...bf, capacity: parseInt(e.target.value) || 30})} className="h-9 rounded-lg text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Start Date</Label><Input type="date" value={bf.start_date} onChange={e => setBf({...bf, start_date: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">End Date</Label><Input type="date" value={bf.end_date} onChange={e => setBf({...bf, end_date: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                </div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Timing</Label><Input data-testid="batch-timing" value={bf.timing} onChange={e => setBf({...bf, timing: e.target.value})} placeholder="e.g. 6:30 PM - 8:30 PM" className="h-9 rounded-lg text-sm" /></div>
                <Button type="submit" data-testid="submit-batch-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="courses">
        <TabsList className="bg-secondary rounded-lg h-9"><TabsTrigger value="courses" data-testid="tab-courses" className="text-xs rounded-md font-mono">Courses ({courses.length})</TabsTrigger><TabsTrigger value="batches" data-testid="tab-batches" className="text-xs rounded-md font-mono">Batches ({batches.length})</TabsTrigger></TabsList>
        <TabsContent value="courses" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Course</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Category</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Duration</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Mode</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider text-right">Price</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider w-[40px]" />
            </TableRow></TableHeader><TableBody>{courses.map(c => {
              const isOpen = expandedCourseId === c.id;
              const stats = enrollmentStats[c.id];
              const isLoading = !!enrollmentLoading[c.id];
              const courseBatches = batches.filter(b => b.course_id === c.id);
              return (
              <Fragment key={c.id}>
                <TableRow
                  data-testid={`course-row-${c.id}`}
                  onClick={() => toggleCourseRow(c.id)}
                  className="cursor-pointer hover:bg-secondary/40 transition-colors"
                >
                  <TableCell className="text-[12px] font-semibold py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      {isOpen ? <CaretUp size={11} className="text-muted-foreground" /> : <CaretDown size={11} className="text-muted-foreground opacity-50" />}
                      {c.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground py-2.5">{c.category || '-'}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{c.duration || '-'}</TableCell>
                  <TableCell className="py-2.5"><span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: MODE_COLOR[c.mode] || '#808080' }}>{c.mode}</span></TableCell>
                  <TableCell className="text-[12px] text-foreground py-2.5 text-right font-mono">Rs.{c.price?.toLocaleString('en-IN')}</TableCell>
                  <TableCell className="py-2.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid={`edit-course-${c.id}`}
                      onClick={() => openEditCourse(c)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit course"
                    >
                      <NotePencil size={13} />
                    </button>
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="bg-secondary/20 hover:bg-secondary/20" data-testid={`course-expand-${c.id}`}>
                    <TableCell colSpan={6} className="py-3 px-4">
                      {isLoading ? (
                        <div className="text-[11px] text-muted-foreground font-mono py-2">loading…</div>
                      ) : !stats ? (
                        <div className="text-[11px] text-muted-foreground font-mono py-2">loading…</div>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <span
                              className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md"
                              style={{ color: '#10A882', backgroundColor: 'rgba(16,168,130,0.08)' }}
                            >
                              <GraduationCap size={13} weight="bold" />
                              {stats.enrolled_count} student{stats.enrolled_count === 1 ? '' : 's'} enrolled
                            </span>
                          </div>
                          <div>
                            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Batches</p>
                            {courseBatches.length === 0 ? (
                              <div className="text-[11px] text-muted-foreground py-1">No batches scheduled for this course yet</div>
                            ) : (
                            <div className="space-y-1.5">
                              {courseBatches.map(b => (
                                <div key={b.id} className="flex items-center gap-2.5" data-testid={`course-expand-batch-${b.id}`}>
                                  <span className="text-[11px] text-foreground font-medium w-40 truncate">{b.name}</span>
                                  <div className="score-bar w-24"><div className="score-bar-fill" style={{ width: `${b.capacity ? (b.enrolled_count / b.capacity) * 100 : 0}%`, backgroundColor: '#E04B2A' }} /></div>
                                  <span className="text-[10px] text-muted-foreground font-mono">{b.enrolled_count}/{b.capacity}</span>
                                </div>
                              ))}
                            </div>
                            )}
                          </div>

                          {stats.students?.length > 0 && (
                            <div className="pt-2 border-t border-border" data-testid={`course-expand-students-${c.id}`}>
                              <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Enrolled Students</p>
                              <div className="space-y-1">
                                {stats.students.map((s, idx) => {
                                  const statusColor = s.status === 'paid' ? '#10A882' : s.status === 'partial' ? '#E5A100' : '#808080';
                                  const clickable = !!s.lead_id;
                                  return (
                                    <div
                                      key={s.invoice_id || idx}
                                      data-testid={`course-expand-student-${s.invoice_id}`}
                                      onClick={clickable ? (e) => { e.stopPropagation(); navigate(`/leads/${s.lead_id}`); } : undefined}
                                      className={`flex items-center justify-between text-[11px] py-1 ${clickable ? 'cursor-pointer hover:text-foreground' : ''}`}
                                    >
                                      <span className={clickable ? 'text-foreground/90' : 'text-muted-foreground'}>{s.student_name || 'Walk-in enrollment'}</span>
                                      <span className="flex items-center gap-2 font-mono text-[10px]">
                                        <span className="text-muted-foreground">Rs.{(s.paid_amount || 0).toLocaleString('en-IN')} / {(s.net_amount || 0).toLocaleString('en-IN')}</span>
                                        <span className="font-bold uppercase tracking-wider" style={{ color: statusColor }}>{s.status || '-'}</span>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
              );
            })}</TableBody></Table>
          </div>
        </TabsContent>
        <TabsContent value="batches" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Batch</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Course</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Branch</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Timing</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Occupancy</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider w-[40px]" />
            </TableRow></TableHeader><TableBody>{batches.map(b => (
              <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                <TableCell className="text-[12px] font-semibold py-2.5">{b.name}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5">{courses.find(c => c.id === b.course_id)?.name || '-'}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5">{branches.find(br => br.id === b.branch_id)?.name || '-'}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{b.timing || '-'}</TableCell>
                <TableCell className="py-2.5"><div className="flex items-center gap-2"><div className="score-bar w-14"><div className="score-bar-fill" style={{width: `${(b.enrolled_count / b.capacity) * 100}%`, backgroundColor: '#E04B2A'}} /></div><span className="text-[10px] text-muted-foreground font-mono">{b.enrolled_count}/{b.capacity}</span></div></TableCell>
                <TableCell className="py-2.5">
                  <button
                    data-testid={`edit-batch-${b.id}`}
                    onClick={() => openEditBatch(b)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit batch"
                  >
                    <NotePencil size={13} />
                  </button>
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editingCourse} onOpenChange={(open) => { if (!open) setEditingCourse(null); }}>
        <DialogContent className="rounded-xl max-w-md" data-testid="edit-course-dialog">
          <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Edit Course</DialogTitle></DialogHeader>
          {ef && (
            <form onSubmit={handleEditCourseSave} className="space-y-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label>
                <Input data-testid="edit-course-name" value={ef.name} onChange={e => setEf({ ...ef, name: e.target.value })} className="h-9 rounded-lg text-sm" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Category</Label>
                  <Input value={ef.category} onChange={e => setEf({ ...ef, category: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Duration (Months)</Label>
                  <Input type="number" min="0" value={ef.duration_months} onChange={e => setEf({ ...ef, duration_months: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Mode</Label>
                  <select value={ef.mode} onChange={e => setEf({ ...ef, mode: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                    <option value="offline">Offline</option><option value="online">Online</option><option value="hybrid">Hybrid</option>
                  </select></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Status</Label>
                  <select value={ef.status} onChange={e => setEf({ ...ef, status: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Lumpsum Fee (Rs.) *</Label>
                  <Input data-testid="edit-course-one-time-fee" type="number" min="0" value={ef.one_time_fee} onChange={e => setEf({ ...ef, one_time_fee: e.target.value })} className="h-9 rounded-lg text-sm" required /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Installment Total (Rs.)</Label>
                  <Input type="number" min="0" value={ef.installment_total} onChange={e => setEf({ ...ef, installment_total: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="Leave blank if no plan" /></div>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Installment Schedule (comma-separated, Rs.)</Label>
                <Input value={ef.installment_schedule} onChange={e => setEf({ ...ef, installment_schedule: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="e.g. 20000, 15000, 10000" />
                <p className="text-[10px] text-muted-foreground mt-1">Must sum to the Installment Total above. Leave blank for Lumpsum-only courses.</p>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branches</Label>
                <div data-testid="edit-course-branch-select" className="flex flex-wrap gap-1.5 mt-1.5 p-2 border border-border rounded-lg bg-card min-h-[36px]">
                  {branches.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleEditBranch(b.id)}
                      className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${
                        ef.branch_ids.includes(b.id) ? 'bg-foreground text-background font-bold' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {b.name}
                    </button>
                  ))}
                  {branches.length === 0 && <span className="text-[10px] text-muted-foreground">No branches</span>}
                </div>
              </div>
              <Button type="submit" disabled={savingEdit} data-testid="save-edit-course-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white disabled:opacity-60" style={{ backgroundColor: '#E04B2A' }}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingBatch} onOpenChange={(open) => { if (!open) setEditingBatch(null); }}>
        <DialogContent className="rounded-xl max-w-md" data-testid="edit-batch-dialog">
          <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Edit Batch</DialogTitle></DialogHeader>
          {ebf && (
            <form onSubmit={handleEditBatchSave} className="space-y-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Course *</Label>
                <select data-testid="edit-batch-course" value={ebf.course_id} onChange={e => setEbf({ ...ebf, course_id: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required>
                  <option value="">Select</option>{courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branch *</Label>
                <select data-testid="edit-batch-branch" value={ebf.branch_id} onChange={e => setEbf({ ...ebf, branch_id: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required>
                  <option value="">Select</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Batch Name *</Label>
                  <Input data-testid="edit-batch-name" value={ebf.name} onChange={e => setEbf({ ...ebf, name: e.target.value })} className="h-9 rounded-lg text-sm" required /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Capacity</Label>
                  <Input type="number" min="0" value={ebf.capacity} onChange={e => setEbf({ ...ebf, capacity: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Start Date</Label>
                  <Input type="date" value={ebf.start_date} onChange={e => setEbf({ ...ebf, start_date: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">End Date</Label>
                  <Input type="date" value={ebf.end_date} onChange={e => setEbf({ ...ebf, end_date: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
              </div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Timing</Label>
                <Input data-testid="edit-batch-timing" value={ebf.timing} onChange={e => setEbf({ ...ebf, timing: e.target.value })} placeholder="e.g. 6:30 PM - 8:30 PM" className="h-9 rounded-lg text-sm" /></div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Status</Label>
                <select data-testid="edit-batch-status" value={ebf.status} onChange={e => setEbf({ ...ebf, status: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select></div>
              <Button type="submit" disabled={savingBatchEdit} data-testid="save-edit-batch-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white disabled:opacity-60" style={{ backgroundColor: '#E04B2A' }}>
                {savingBatchEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
